import { type SEOHeadProps } from '@/features/shared/components/SEOHeader/types'
import Head from 'next/head'
import { SEO_CONSTANTS } from '@/features/shared/utils/seo/seoConfig'
import { buildSeoJsonLd } from '@/features/shared/utils/seo/buildSeoJsonLd'
import { serializeJSONForScript } from '@/features/shared/utils/serializeJSONForScript'

export const SEOHead = ({
  title,
  description,
  canonicalUrl,
  doNotFollow,
  doNotIndex,
  image = SEO_CONSTANTS.defaultSeoImage,
  breadcrumbs,
  socialMediaSchema,
  pageTypeSchema,
  additionalSchemas,
}: SEOHeadProps) => {
  const getRobotsContent = () => {
    if ((doNotIndex && doNotFollow) || process.env.NEXT_PUBLIC_ENV !== 'production')
      return 'noindex,nofollow'
    if (doNotIndex) return 'noindex'
    if (doNotFollow) return 'nofollow'
    return 'max-snippet:-1, max-image-preview:large, max-video-preview:-1'
  }

  // Open Graph metadata is used by social media platforms to display rich previews of your pages.
  // If Open Graph metadata is not provided, we create a default one with the title,
  socialMediaSchema ??= {
    title,
    description,
    url: canonicalUrl,
    type: SEO_CONSTANTS.ogType,
    image,
    card: SEO_CONSTANTS.twitterCard,
  }

  return (
    <Head>
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonicalUrl} />
      <meta name="viewport" content="width=device-width, initial-scale=1" />
      <meta charSet="UTF-8" />
      <meta name="robots" content={getRobotsContent()} />
      {/* Open Graph metadata */}
      <meta property="og:title" content={socialMediaSchema.title} />
      <meta property="og:locale" content={SEO_CONSTANTS.ogLocale} />
      <meta property="og:description" content={socialMediaSchema.description} />
      <meta property="og:url" content={socialMediaSchema.url} />
      <meta property="og:type" content={socialMediaSchema.type} />
      <meta property="og:image" content={socialMediaSchema.image.url} />
      {Boolean(socialMediaSchema.image.width && socialMediaSchema.image.height) && (
        <>
          <meta property="og:image:width" content={String(socialMediaSchema.image.width)} />
          <meta property="og:image:height" content={String(socialMediaSchema.image.height)} />
        </>
      )}
      <meta property="og:site_name" content={SEO_CONSTANTS.ogSiteName} />
      {socialMediaSchema.type === 'product' && (
        <>
          <meta property="product:price:amount" content={socialMediaSchema.price} />
          <meta property="product:price:currency" content="USD" />
        </>
      )}
      {/* Twitter metadata */}
      <meta name="twitter:card" content={socialMediaSchema.card} />
      <meta name="twitter:title" content={socialMediaSchema.title} />
      <meta name="twitter:description" content={socialMediaSchema.description} />
      <meta name="twitter:image" content={socialMediaSchema.image.url} />
      <meta name="twitter:site" content={SEO_CONSTANTS.twitterHandle} />
      <meta name="twitter:creator" content={SEO_CONSTANTS.twitterHandle} />
      <meta name="google-site-verification" content={SEO_CONSTANTS.googleVerification} />
      {/* Must be a native <script>, not a wrapper component: next/head only
          injects native head element types on the client, so a component child
          would be dropped on client-only/CSR renders.
          https://nextjs.org/docs/pages/api-reference/components/head#use-minimal-nesting */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: serializeJSONForScript(
            buildSeoJsonLd({
              title,
              description,
              canonicalUrl,
              image,
              breadcrumbs,
              pageTypeSchema,
              additionalSchemas,
            }),
          ),
        }}
      />
    </Head>
  )
}
